import DataStorage from "./DataStorage";
import {
  getDimensions,
  hasFrontAndBack,
  isApparel,
  isArtwork,
} from "./productHelpers";
import productValues from "../assets/fabricProductValues";
import {
  checkMultipleCanvas,
  getProductType,
} from "../redux/creators/threekitCreators";
import { getFile } from "../redux/creators/fileCreators";
import wallDisplay from "../../../config/wallDisplayStyle";
import collage from "../../../config/collageStyle";

// Create base fabric canvas
export function createCanvas(id) {
  return new fabric.Canvas(id, {
    height: 475,
    width: 610,
    fill: "#bcd0d6",
    controlsAboveOverlay: true,
  });
}

// Find object by name
export function findByName(group, name) {
  return group.getObjects().find((item) => item.name === name);
}

// Retrieves canvas from data storage
export function getCanvas(state) {
  const canvases = {};
  Object.keys(state.fabric.canvases).forEach((key) => {
    canvases[key] = DataStorage.find(state.fabric.canvases[key]);
  });
  const canvas = DataStorage.find(state.fabric.canvas);
  return { canvas, canvases };
}

// Gets the correct current canvas
export function getCurrentCanvas(state, node) {
  const isMultipleFabricCanvas = hasFrontAndBack(getProductType(state));
  const { canvas, canvases } = getCanvas(state);
  const currentNode = node || state.playerState.currentNode;
  return isMultipleFabricCanvas ? canvases[currentNode] : canvas;
}

// Get canvas objects
function getObjects(canvas) {
  if (canvas.getObjects()[0].name === "maskGroup") {
    return canvas.getObjects()[0].getObjects();
  } else {
    return canvas.getObjects();
  }
}

// Set active image
export function setActiveImage(canvas, name) {
  return () => {
    const items = getObjects(canvas);
    const selectedImage = items.find((item) => item.name == "img_" + name);
    selectedImage && canvas.setActiveObject(selectedImage);
  };
}

export function addUploadIconAndRender(mask, canvas, group, onMouseUp) {
  fabric.Image.fromURL(
    "https://cdn.shopify.com/s/files/1/0591/4299/6149/files/edit-image.png",
    function (img) {
      img.name = group ? "uploadIcon_" + mask.name : "uploadIcon";
      // finds center point of mask
      // if the mask is the same as the group, it'll be centered within the group by default
      const centerPoint =
        mask.name === group?.name ? { x: 0, y: 0 } : mask.getCenterPoint();
      // adds the image to the canvas
      group ? group.add(img) : canvas.add(img);
      // makes the image unselectable
      img.selectable = false;
      let { width, height } = mask;
      if (window.collectionName.includes("triangle")) {
        width = width * 0.6;
        height = height * 0.6;
      }
      if (width > height) {
        img.scaleToHeight(height);
      } else {
        img.scaleToWidth(width);
      }
      // tells the image to center itself in the center of the centerpoint of the mask
      img.setPositionByOrigin(centerPoint, "center", "center");
      let target = mask.getObjects ? mask : img;
      if (!window.collectionName.includes("triptych")) {
        target.on("mouseup", function (e) {
          onMouseUp
            ? onMouseUp()
            : document.getElementById("threekit-fileupload").click();
        });
      }
      canvas.renderAll();
    },
    { crossOrigin: "Anonymous" }
  );
}

export function getSelectedImage(canvas, nodeName) {
  return canvas
    .getObjects()
    .find((item) => item.name === "maskGroup")
    .getObjects()
    .find((item) => item.name === `img_${nodeName}`);
}

export function dimensionsToPixelDimensions(
  [canHeight, canWidth],
  productType
) {
  let height, width;
  const displayRatio = canHeight / canWidth;

  if (productType.includes("triptych")) {
    height = productValues[productType].height;
    width = productValues[productType].width;
    return [height, width];
  }

  if (parseInt(canHeight) >= parseInt(canWidth)) {
    if (
      productType == "single-canvas" ||
      productType == "poster" ||
      productType == "metal"
    ) {
      if (canHeight > 7 && canHeight < 13) {
        height = productValues[productType]["XS"].defaultHeight;
      } else if (canHeight > 12 && canHeight < 21) {
        height = productValues[productType]["S"].defaultHeight;
      } else if (canHeight > 20 && canHeight < 41) {
        height = productValues[productType]["M"].defaultHeight;
      } else {
        height = productValues[productType]["L"].defaultHeight;
      }
    } else {
      height = productValues[productType].defaultHeight;
    }
    width = height / displayRatio;
  } else {
    if (
      productType == "single-canvas" ||
      productType == "poster" ||
      productType == "metal"
    ) {
      if (canWidth > 7 && canWidth < 12) {
        width = productValues[productType]["XS"].defaultWidth;
      } else if (canWidth > 12 && canWidth < 21) {
        width = productValues[productType]["S"].defaultWidth;
      } else if (canWidth > 20 && canWidth < 41) {
        width = productValues[productType]["M"].defaultWidth;
      } else {
        width = productValues[productType]["L"].defaultWidth;
      }
    } else {
      width = productValues[productType].defaultWidth;
    }
    height = width * displayRatio;
  }

  return [height, width];
}

export function maskDimensions(state) {
  const productType = getProductType(state);
  const selectedProduct = state.threekit.selectedProduct || window.product;
  let width, height;
  if (isApparel(productType)) {
    const size = selectedProduct.option1 || "S";
    width = productValues[productType][size].width;
    height = productValues[productType][size].height;
  } else if (productValues[productType].config?.noDimensions) {
    return [0, 0];
  } else if (
    selectedProduct &&
    getDimensions(selectedProduct) &&
    getDimensions(selectedProduct).length > 1 &&
    !isArtwork(selectedProduct)
  ) {
    if (state.playerState?.dimensions?.canWidth) {
      const { canWidth, canHeight } = state.playerState.dimensions;
      [height, width] = dimensionsToPixelDimensions(
        [canHeight, canWidth],
        productType
      );
    } else {
      [height, width] = dimensionsToPixelDimensions(
        getDimensions(selectedProduct),
        productType
      );
      productValues[productType].width = width;
      productValues[productType].height = height;
    }
  } else {
    width = productValues[productType].width;
    height = productValues[productType].height;
  }
  return [width, height];
}

export function canDimensions(state) {
  const productType = getProductType(state);
  const selectedProduct = state.threekit.selectedProduct || window.product;
  let canHeight, canWidth;
  const productVals = productValues[productType];
  if (productVals.config?.noDimensions) {
    return [0, 0];
  }
  if (isApparel(productType)) {
    const size = selectedProduct.option1 || "S";
    canWidth = productVals[size].canWidth;
    canHeight = productVals[size].canHeight;
  } else if (productVals.canWidth && productVals.canHeight) {
    canHeight = productVals.canHeight;
    canWidth = productVals.canWidth;
  } else {
    const initialDimensions = getDimensions(selectedProduct)?.filter(
      (dimension) => !isNaN(dimension)
    );
    canHeight = initialDimensions ? initialDimensions[0] : 0;
    canWidth = initialDimensions ? initialDimensions[1] : 0;
  }

  return [canWidth ?? 0, canHeight ?? 0];
}

function getMultipleMaskObject(productType, key, width, height) {
  const fill = productValues[productType].maskColor;
  if (productType == "coaster") {
    return new fabric.Circle({
      name: "mask_" + key,
      radius: width / 2,
      fill,
      absolutePositioned: true,
    });
  } else if (productType == "ornament") {
    const ball = new fabric.Circle({
      name: "mask_" + key,
      radius: width / 2,
      fill,
      strokeWidth: 0,
      absolutePositioned: true,
    });
    const top = new fabric.Rect({
      name: "mask_" + key,
      width: 35,
      height: 31,
      top: -29.1666666667,
      left: 70,
      fill,
      strokeWidth: 0,
      absolutePositioned: true,
    });
    return new fabric.Group([ball, top], {
      name: "mask_" + key,
      fill,
      strokeWidth: 0,
      absolutePositioned: true,
    });
  } else {
    return new fabric.Rect({
      name: "mask_" + key,
      width,
      height,
      fill,
      absolutePositioned: true,
    });
  }
}

function getMasksForMultipleCanvas(state) {
  const productType = getProductType(state);
  let masksObject = productValues[productType].masks;
  if (productType == "tile") {
    const size = state.threekit.selectedProduct.option1 || "5";
    masksObject = productValues[productType].masks[size];
  } else if (productType == "photos") {
    const orientation = state.threekit.selectedProduct.option1 || '4" x 6"';
    masksObject = productValues[productType].masks[orientation];
  }
  return Object.keys(masksObject).map((key) => {
    const { width, height, left, top } = masksObject[key];
    const obj = getMultipleMaskObject(productType, key, width, height);
    return new fabric.Group([obj], {
      name: key,
      left,
      top,
      selectable: false,
      strokeWidth: 0,
      absolutePositioned: true,
    });
  });
}

export function getApparelMaskFill(state) {
  const productType = getProductType(state);
  const color = state.threekit.selectedProduct.option2 || "White";
  return productValues[productType].maskColor[color.split(" ")[0]];
}

/**
 * This is for using an image for a mask instead of a fabric shape
 * Currently this is just using an image for the bottle opener
 * if more products need it we will want to switch to using
 * a function or a product value to get the correct image url
 */
export function getImageMask(canvas, url) {
  new fabric.Image.fromURL(
    url,
    (maskImage) => {
      maskImage.set({
        name: "mask",
        selectable: false,
        absolutePositioned: true,
      });
      // Set the mask image as the clip path for the canvas
      canvas.clipPath = maskImage;

      // Add the mask image to the canvas
      canvas.add(maskImage).centerObject(maskImage);

      // Render the canvas
      canvas.renderAll();

      // On click launch file upload
      maskImage.on("mouseup", function () {
        document.getElementById("threekit-fileupload").click();
      });
    },
    { crossOrigin: "Anonymous" }
  );
}

export function getMask(state, renderMask) {
  const productType = getProductType(state);
  const [width, height] = maskDimensions(state, productType);
  const fill = isApparel(productType)
    ? getApparelMaskFill(state)
    : productValues[productType].maskColor;
  let mask;
  let masks;
  let rendered = false;

  if (productType.includes("mug")) {
    /**
     * Fabric path using svg path pattern
     * https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths
     */

    const path = productType.includes("camping")
      ? "M 0 0 C 65 22, 195 22, 250 -5 L 250 -5 L 250 163 C 195 200, 65 200, 5 170 L 5 170 L 0 0"
      : "M 0 0 C 0 5, 223 5, 228 0 L 228 0 L 226 204 C 204 236, 0 236, 6 204 L 6 204 L 0 0";
    mask = new fabric.Path(path, {
      name: "mask",
      fill,
      selectable: false,
      absolutePositioned: true,
    });
  } else if (checkMultipleCanvas(state) && !hasFrontAndBack(productType)) {
    masks = getMasksForMultipleCanvas(state);
  } else if (productType.includes("triptych")) {
    const left = new fabric.Rect({
      name: "mask_left",
      width,
      height: productValues[productType].minHeight || height,
      fill,
      top: productValues[productType].top,
      left: 0,
      selectable: false,
      absolutePositioned: true,
    });
    const center = new fabric.Rect({
      name: "mask_center",
      width,
      height,
      fill,
      top: 0,
      left: 140,
      selectable: false,
      absolutePositioned: true,
    });
    const right = new fabric.Rect({
      name: "mask_right",
      width,
      height: productValues[productType].minHeight || height,
      fill,
      top: productValues[productType].top,
      left: 280,
      selectable: false,
      absolutePositioned: true,
    });
    mask = new fabric.Group([left, center, right], {
      name: "maskGroup",
      selectable: false,
      absolutePositioned: true,
      originX: "left",
      originY: "top",
    });
  } else if (
    window.collectionName.includes("circle") ||
    window.collectionName.includes("pulp-coaster-round")
  ) {
    mask = new fabric.Circle({
      radius: width / 2,
      name: "mask",
      fill,
      selectable: false,
      absolutePositioned: true,
    });
  } else if (window.collectionName.includes("oval")) {
    mask = new fabric.Ellipse({
      name: "mask",
      rx: width / 2,
      ry: height / 2,
      fill,
      selectable: false,
      absolutePositioned: true,
    });
  } else if (window.collectionName.includes("triangle")) {
    mask = new fabric.Path("M 0 300 L 300 300 L 150 0 Z", {
      name: "mask",
      fill,
      width: width,
      height: height,
      selectable: false,
      absolutePositioned: true,
    });
  } else {
    let name = checkMultipleCanvas(state) ? "mask_1" : "mask";
    mask = new fabric.Rect({
      name,
      width,
      height,
      fill,
      selectable: false,
      absolutePositioned: true,
    });
  }

  // Rounded corners
  if (productType == "mousepad") {
    mask.set({ rx: 10, ry: 10 });
  }

  if (productType == "glasscuttingboard") {
    mask.set({ rx: 5, ry: 5 });
  }

  !rendered && renderMask(masks || mask);
}

export function getImageIds(state) {
  return checkMultipleCanvas(state)
    ? state.fabric.imageIds
    : { imageId: state.fabric.imageId };
}

export function triptychImage(name, top, left, ratio) {
  return {
    name,
    top,
    left,
    scaleX: ratio,
    scaleY: ratio,
    originX: "center",
    originY: "center",
    hasControls: false,
    selectable: false,
  };
}

export function addProductTexture(state, canvas) {
  const productType = getProductType(state);
  const texture = productValues[productType].texture;
  let source = texture.source;
  let opacity = texture.opacity;

  if (productType == "poster") {
    // Get poster paper type
    const option = state.threekit.options.find(
      (opt) => opt.handle == "paper-type"
    );
    const paperType = option.values.find((value) => value.selected);
    // Set texture based on paper type
    source = texture[paperType.value].source;
    opacity = texture[paperType.value].opacity;
  }

  setOverlayImage(canvas, source, opacity);
}

// Add overlay texture image to canvas
export function setOverlayImage(canvas, source, opacity) {
  canvas.setOverlayImage(source, canvas.renderAll.bind(canvas), {
    originX: "left",
    originY: "top",
    opacity: opacity,
    crossOrigin: "anonymous",
    globalCompositeOperation: "source-atop",
  });
}

export function hasFileUploaded(state, node) {
  const urlParams = new URLSearchParams(window.location.search);
  return (
    getFile(state).file ||
    (node && getFile(state).files[node]) ||
    (urlParams.get("front") && node == "front") ||
    (urlParams.get("back") && node == "back")
  );
}

export function getNodeDimensions(state, node) {
  const productType = getProductType(state);
  const canHeight = productValues[productType].canHeight;
  const canWidth = productValues[productType].canWidth;

  // Wall display and collage need dimensions set based on node
  if (productType == "wall-display" || productType == "collage") {
    const type = productType == "wall-display" ? wallDisplay : collage;
    return type[node].dimensions;
    // Use values set in product's productValues
  } else if (canHeight && canWidth) {
    return canHeight + "x" + canWidth;
  } else {
    const dimensions = canDimensions(state);
    return dimensions[1] + "x" + dimensions[0];
  }
}

/**
 * Returns html color to use for a selected frame
 *
 * @param {string} color   Color of selected frame
 *
 * @returns {string}
 */
export function getFrameColor(color) {
  switch (color) {
    case "barnwood":
      return "#474742";
    case "black":
      return "black";
    case "espresso":
      return "#433430";
    //case "gold":
      //return "#B1895A";
    case "natural":
      return "#E1C598";
    case "white":
      return "white";
    default:
      return "";
  }
}

/**
 * Update the color of apparel masks
 * @param {Object} state State values
 */
export function updateMaskColors(state) {
  const { canvas, canvases } = getCanvas(state);
  const color = getApparelMaskFill(state);

  // Set fill color for mask object
  function setColor(can) {
    const mask = can.getObjects()[0];
    mask.set({ fill: color });
    can.renderAll();
  }

  // On each existing canvas, update the mask color
  canvases && Object.values(canvases).forEach((c) => setColor(c));
  canvas && setColor(canvas);
}
